import React, { useState, useEffect, useCallback, useRef } from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js-dist-min';
import { getAdminOverview, getAdminDailySeries } from '../../adminApi';

const Plot = createPlotlyComponent(Plotly);

const STAT_ROWS = [
  [
    { key: 'active_companies', label: 'Active Companies', fmt: 'int' },
    { key: 'active_users',     label: 'Active Users',     fmt: 'int' },
    { key: 'total_queries',    label: 'Total Queries',    fmt: 'int' },
    { key: 'success_rate',     label: 'Success Rate',     fmt: 'pct' },
  ],
  [
    { key: 'total_tokens',        label: 'Total Tokens',     fmt: 'int'  },
    { key: 'total_cost_usd',      label: 'Total Cost',       fmt: 'usd'  },
    { key: 'avg_evaluator_score', label: 'Avg Eval Score',   fmt: 'dec2' },
    { key: 'thumbs_up_pct',       label: 'Thumbs Up %',      fmt: 'pct'  },
  ],
  [
    { key: 'queries_today',       label: 'Queries Today',       fmt: 'int' },
    { key: 'queries_this_week',   label: 'Queries This Week',   fmt: 'int' },
    { key: 'avg_response_time_ms',label: 'Avg Response Time',   fmt: 'ms'  },
  ],
];

const BUCKETS = [
  { label: 'Day',     bucket: 'day',     days: 30,   title: '30-Day Trend'    },
  { label: 'Month',   bucket: 'month',   days: 365,  title: 'Monthly Trend'   },
  { label: 'Quarter', bucket: 'quarter', days: 1830, title: 'Quarterly Trend' },
];

// '' means all sources: the API treats an absent `source` as no filter.
const SOURCES = [
  { label: 'All',      value: ''         },
  { label: 'Chat',     value: 'chat'     },
  { label: 'Describe', value: 'describe' },
];

const BY_SOURCE_CARDS = [
  { key: 'chat',     label: 'Chat Requests'     },
  { key: 'describe', label: 'Describe Requests' },
];

function findBucket(bucket) {
  return BUCKETS.find(b => b.bucket === bucket) || BUCKETS[0];
}

function findSource(source) {
  return SOURCES.find(s => s.value === source) || SOURCES[0];
}

function fmtVal(value, type) {
  if (value === null || value === undefined) return '—';
  switch (type) {
    case 'int':  return Number(value).toLocaleString();
    case 'pct':  return `${(value * 100).toFixed(1)}%`;
    case 'usd':  return `$${Number(value).toFixed(2)}`;
    case 'dec2': return Number(value).toFixed(2);
    case 'ms':   return `${Math.round(value).toLocaleString()} ms`;
    default:     return String(value);
  }
}

function SkeletonCard() {
  return (
    <div className="stat-card">
      <div className="admin-skeleton admin-skeleton-label" />
      <div className="admin-skeleton admin-skeleton-value" />
    </div>
  );
}

export default function AdminOverview() {
  const [overview, setOverview] = useState(null);
  // The bucket and source travel with the points so the title and axis type always
  // match the data on screen, even while a fetch for a new selection is in flight.
  const [series, setSeries]     = useState({ bucket: 'day', source: '', points: [] });
  const [bucket, setBucket]     = useState('day');
  const [source, setSource]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const latestRequest           = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++latestRequest.current;
    const { days } = findBucket(bucket);
    try {
      const [ov, sr] = await Promise.all([
        getAdminOverview(),
        getAdminDailySeries({ days, bucket, source: source || undefined }),
      ]);
      // A slower response for a previous selection must not overwrite a newer one.
      if (requestId !== latestRequest.current) return;
      setOverview(ov);
      setSeries({ bucket, source, points: sr });
      setError('');
    } catch (err) {
      if (requestId !== latestRequest.current) return;
      setError(err.message || 'Failed to load overview');
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [bucket, source]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  if (loading) {
    return (
      <div>
        <h2 className="admin-section-title">Overview</h2>
        {STAT_ROWS.map((row, i) => (
          <div
            key={i}
            className="admin-stat-grid"
            style={{ gridTemplateColumns: `repeat(${row.length}, 1fr)` }}
          >
            {row.map((_, j) => <SkeletonCard key={j} />)}
          </div>
        ))}
        <div className="admin-chart-card admin-skeleton-chart" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h2 className="admin-section-title">Overview</h2>
        <div className="admin-error-state">
          <p>{error}</p>
          <button onClick={() => { setLoading(true); load(); }}>Retry</button>
        </div>
      </div>
    );
  }

  const points      = series.points;
  const shownBucket = findBucket(series.bucket);
  const shownSource = findSource(series.source);
  const chartTitle  = series.source ? `${shownBucket.title} — ${shownSource.label}` : shownBucket.title;
  // Absent on a backend that predates the source column; the row is then omitted.
  const bySource    = overview?.by_source;
  // "2026-07" and "2026-Q3" are labels, not dates, so they must not be auto-typed.
  // '-' is Plotly's auto-detect (the day view's behaviour) and is set explicitly so
  // switching back from a category bucket re-runs detection.
  const xaxisType   = shownBucket.bucket === 'day' ? '-' : 'category';

  const chartData = [
    {
      x: points.map(d => d.date),
      y: points.map(d => d.query_count),
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Queries',
      line: { color: '#2563eb', width: 2 },
      marker: { size: 4 },
      yaxis: 'y1',
    },
    {
      x: points.map(d => d.date),
      y: points.map(d => d.token_count),
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Tokens',
      line: { color: '#10b981', width: 2, dash: 'dot' },
      marker: { size: 4 },
      yaxis: 'y2',
    },
  ];

  const chartLayout = {
    autosize: true,
    height: 280,
    margin: { t: 20, r: 60, b: 40, l: 50 },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: { family: 'Montserrat, sans-serif', size: 12, color: '#6b7280' },
    legend: { x: 0, y: 1.18, orientation: 'h' },
    xaxis: { showgrid: false, tickfont: { size: 11 }, type: xaxisType },
    yaxis: {
      title: 'Queries',
      showgrid: true,
      gridcolor: '#f3f4f6',
      tickfont: { size: 11 },
    },
    yaxis2: {
      title: 'Tokens',
      overlaying: 'y',
      side: 'right',
      showgrid: false,
      tickfont: { size: 11 },
    },
  };

  return (
    <div>
      <h2 className="admin-section-title">Overview</h2>

      {STAT_ROWS.map((row, i) => (
        <div
          key={i}
          className="admin-stat-grid"
          style={{ gridTemplateColumns: `repeat(${row.length}, 1fr)` }}
        >
          {row.map(({ key, label, fmt }) => (
            <div key={key} className="stat-card">
              <div className="stat-card-label">{label}</div>
              <div className="stat-card-value">{fmtVal(overview?.[key], fmt)}</div>
            </div>
          ))}
        </div>
      ))}

      {bySource && (
        <div
          className="admin-stat-grid"
          style={{ gridTemplateColumns: `repeat(${BY_SOURCE_CARDS.length}, 1fr)` }}
        >
          {BY_SOURCE_CARDS.map(({ key, label }) => (
            <div key={key} className="stat-card">
              <div className="stat-card-label">{label}</div>
              <div className="stat-card-value">{fmtVal(bySource[key]?.requests, 'int')}</div>
              <div className="stat-card-sub">
                {fmtVal(bySource[key]?.tokens, 'int')} tokens · {fmtVal(bySource[key]?.cost_usd, 'usd')}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="admin-chart-card">
        <div className="admin-toolbar" style={{ marginBottom: 8 }}>
          <div className="admin-chart-title" style={{ margin: 0 }}>{chartTitle}</div>
          <div className="admin-toolbar-controls">
            <div className="admin-period-btns" role="group" aria-label="Trend source">
              {SOURCES.map(s => (
                <button
                  key={s.value}
                  type="button"
                  className={`admin-period-btn${source === s.value ? ' active' : ''}`}
                  aria-pressed={source === s.value}
                  onClick={() => setSource(s.value)}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <div className="admin-period-btns" role="group" aria-label="Trend granularity">
              {BUCKETS.map(b => (
                <button
                  key={b.bucket}
                  type="button"
                  className={`admin-period-btn${bucket === b.bucket ? ' active' : ''}`}
                  aria-pressed={bucket === b.bucket}
                  onClick={() => setBucket(b.bucket)}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <Plot
          data={chartData}
          layout={chartLayout}
          config={{ displayModeBar: false, responsive: true }}
          style={{ width: '100%' }}
          useResizeHandler
        />
      </div>
    </div>
  );
}
